import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { X, AlertTriangle } from "lucide-react";

const REASONS = [
  "Too expensive right now",
  "My child doesn't have time to practise",
  "I'm not seeing enough progress",
  "Technical issues in the app",
  "Switching to a different school",
  "Other",
];

export default function CancelMembershipDialog({ open, onClose }) {
  const [reason, setReason] = useState(REASONS[0]);
  const [details, setDetails] = useState("");
  const [pending, setPending] = useState(false);
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDetails("");
    setReason(REASONS[0]);
    api.get("/membership/cancel/status")
      .then(({ data }) => setExisting(data.pending ? data : null))
      .catch(() => {});
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    const composed = `${reason}${details.trim() ? " — " + details.trim() : ""}`;
    if (composed.length < 5) { toast.error("Please tell us a bit more."); return; }
    setPending(true);
    try {
      await api.post("/membership/cancel", { reason: composed });
      toast.success("Cancellation request submitted. Your teacher will review it shortly.");
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="cancel-membership-dialog" className="bg-white rounded-3xl w-full max-w-lg p-6 card-tactile" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-watermelon-light text-watermelon flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-heading text-2xl font-bold text-ink">Cancel membership</h3>
              <p className="text-sm text-stone">We're sad to see you go — one quick question first.</p>
            </div>
          </div>
          <button data-testid="cancel-dialog-close" onClick={onClose} className="text-stone hover:text-ink"><X className="w-5 h-5" /></button>
        </div>

        {existing ? (
          <div className="p-4 rounded-2xl bg-lemon/30 border-2 border-lemon">
            <p className="text-sm text-ink"><strong>A cancellation is already pending.</strong> Your teacher will review "{existing.reason}" and get back to you.</p>
          </div>
        ) : (
          <>
            <label className="block text-sm font-bold text-ink mb-2">Why are you cancelling?</label>
            <div className="space-y-2 mb-4">
              {REASONS.map((r) => (
                <label key={r} className={`flex items-center gap-3 p-3 rounded-2xl border-2 cursor-pointer ${reason === r ? "border-sky bg-sky-light/50" : "border-cloud hover:border-sky/50"}`}>
                  <input data-testid={`cancel-reason-${r.slice(0,10)}`} type="radio" name="reason" value={r} checked={reason === r} onChange={(e) => setReason(e.target.value)} className="w-4 h-4 accent-sky" />
                  <span className="text-sm font-semibold text-ink">{r}</span>
                </label>
              ))}
            </div>
            <label className="block text-sm font-bold text-ink mb-2">Anything else you'd like your teacher to know? <span className="text-stone font-normal">(optional)</span></label>
            <textarea
              data-testid="cancel-details-input"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Tell us more so we can improve…"
              className="w-full p-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none text-sm bg-white resize-none mb-4"
            />
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 h-12 rounded-full bg-white border-2 border-cloud text-ink font-bold hover:bg-canvas">Keep membership</button>
              <button
                data-testid="submit-cancel-request"
                onClick={submit}
                disabled={pending}
                className="flex-1 h-12 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover disabled:opacity-70"
              >
                {pending ? "Submitting…" : "Submit request"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
